import { z } from 'zod';
import { toFlatField } from '../oasis-types';
export const baseM1060 = z.object({
    m1060a: z.string().nullable(),
    m1060b: z.string().nullable(),
});
export const baseM1060Refined = z.object({
    m1060a: z.string().refine((data) => {
        const value = Number(data);
        if (isNaN(value) || value < 50 || value > 80) {
            return false;
        }
        return true;
    }, {
        message: 'Please enter a value between 50 and 80',
        path: [],
    }),
    m1060b: z.string().refine((data) => {
        const value = Number(data);
        if (isNaN(value) || value < 65 || value > 440) {
            return false;
        }
        return true;
    }, {
        message: 'Please enter a value between 65 and 440',
        path: [],
    }),
});
export const baseWeightClinical = z.object({
    wnl: z.boolean().optional(),
    heightSource: z.string().optional(),
    weightSource: z.string().optional(),
    previousWeight: z.string().optional(),
    previousWeightDate: z.string().optional(),
    previousWeightSource: z.string().optional(),
    weightChange: z.string().optional(),
    weightChangeAmount: z.string().optional(),
    weightChangePeriod: z.string().optional(),
    weightChangePeriodOther: z.string().optional(),
    changeIntentional: z.string().optional(),
    weightLossCauses: z.array(z.string()).optional(),
    weightGainCauses: z.array(z.string()).optional(),
    weightGainSymptoms: z.array(z.string()).optional(),
    monitoringReasons: z.array(z.string()).optional(),
    clinicalNarrative: z.string().optional(),
});
export const baseK0520Clinical = z.object({
    wnl: z.boolean().optional(),
    parenteral: z
        .object({
        type: z.array(z.string()).optional(),
        access: z.array(z.string()).optional(),
        formula: z.string().optional(),
        rate: z.string().optional(),
        hoursPerDay: z.string().optional(),
        caloriesPerDay: z.string().optional(),
        monitoring: z.array(z.string()).optional(),
        monitoringFrequency: z.string().optional(),
        complications: z.array(z.string()).optional(),
    })
        .optional(),
    feedingTube: z
        .object({
        tubeType: z.array(z.string()).optional(),
        size: z.string().optional(),
        placedDate: z.string().optional(),
        formula: z.string().optional(),
        rate: z.string().optional(),
        method: z.array(z.string()).optional(),
        schedule: z.string().optional(),
        totalVolumePerDay: z.string().optional(),
        freeWaterFlushes: z.string().optional(),
        tubeSite: z.array(z.string()).optional(),
        residuals: z.string().optional(),
        residualThreshold: z.string().optional(),
        residualAction: z.array(z.string()).optional(),
        patientCompetency: z.string().optional(),
        skilledJustification: z.array(z.string()).optional(),
    })
        .optional(),
    mechanicallyAlteredDiet: z
        .object({
        foodTexture: z.array(z.string()).optional(),
        liquidConsistency: z.array(z.string()).optional(),
        reason: z.array(z.string()).optional(),
        reasonOther: z.string().optional(),
        swallowEval: z.string().optional(),
        swallowEvalDate: z.string().optional(),
        dietCompliance: z.string().optional(),
    })
        .optional(),
    therapeuticDiet: z
        .object({
        dietTypes: z.array(z.string()).optional(),
        carbsPerMeal: z.string().optional(),
        sodiumPerDay: z.string().optional(),
        fluidRestriction: z.string().optional(),
        dietTypeOther: z.string().optional(),
        prescribedBy: z.array(z.string()).optional(),
        educationProvided: z.string().optional(),
        educationDate: z.string().optional(),
        dietCompliance: z.string().optional(),
        complianceBarriers: z.array(z.string()).optional(),
        skilledJustification: z.array(z.string()).optional(),
    })
        .optional(),
});
export const baseOralIntakeClinical = z.object({
    wnl: z.boolean().optional(),
    appetite: z
        .object({
        currentAppetite: z.string().optional(),
        appetiteChange: z.string().optional(),
        appetiteChangeSince: z.string().optional(),
        appetiteChangeCause: z.string().optional(),
        mealsPerDay: z.string().optional(),
        typicalIntake: z.string().optional(),
        fluidIntake: z.string().optional(),
    })
        .optional(),
    swallowing: z
        .object({
        swallowingDifficulty: z.array(z.string()).optional(),
        aspirationRiskSigns: z.array(z.string()).optional(),
        aspirationPneumoniaHistory: z.string().optional(),
        aspirationPneumoniaWhen: z.string().optional(),
        aspirationPneumoniaEpisodes: z.string().optional(),
        slpEvaluation: z.string().optional(),
        slpEvaluationDate: z.string().optional(),
    })
        .optional(),
    factorsAffecting: z
        .object({
        physical: z.array(z.string()).optional(),
        sensory: z.array(z.string()).optional(),
        cognitivePsych: z.array(z.string()).optional(),
        functional: z.array(z.string()).optional(),
        medicationsAffecting: z.string().optional(),
        medicationsAffectingDetails: z.string().optional(),
        socialEconomic: z.array(z.string()).optional(),
    })
        .optional(),
});
export const baseEatingSupport = z.object({
    wnl: z.boolean().optional(),
    adaptiveEquipment: z
        .object({
        equipmentInUse: z.array(z.string()).optional(),
        equipmentInUseOther: z.string().optional(),
        equipmentNeeded: z.array(z.string()).optional(),
        equipmentNeededList: z.string().optional(),
    })
        .optional(),
    mealAssistance: z
        .object({
        assistanceProvider: z.array(z.string()).optional(),
        helpType: z.array(z.string()).optional(),
        caregiverTrainingNeeded: z.string().optional(),
        caregiverTrainingTopics: z.array(z.string()).optional(),
        caregiverTrainingOther: z.string().optional(),
    })
        .optional(),
});
export const basePocNutrition = z.object({
    skilledJustification: z.array(z.string()).optional(),
});
export const baseK0520 = z.object({
    a1: z.boolean().optional(),
    b1: z.boolean().optional(),
    c1: z.boolean().optional(),
    d1: z.boolean().optional(),
    z1: z.boolean().optional(),
    a4: z.boolean().optional(),
    b4: z.boolean().optional(),
    c4: z.boolean().optional(),
    d4: z.boolean().optional(),
    z4: z.boolean().optional(),
    a5: z.boolean().optional(),
    b5: z.boolean().optional(),
    c5: z.boolean().optional(),
    d5: z.boolean().optional(),
    z5: z.boolean().optional(),
});
export const SectionKVersioned = {
    m1060: { e1: { alias: 'm1060', timepoint: ['soc', 'roc'], tags: ['PRA'] } },
    weightClinical: { e1: { alias: 'weightClinical', timepoint: ['soc', 'roc'] } },
    k0520Clinical: { e1: { alias: 'k0520Clinical', timepoint: ['soc', 'roc', 'dsch'] } },
    oralIntakeClinical: { e1: { alias: 'oralIntakeClinical', timepoint: ['soc', 'roc'] } },
    k0520: {
        e1: {
            alias: 'k0520',
            timepoint: ['soc', 'roc', 'dsch'],
            tags: ['PRA'],
            fields: {
                a1: { timepoint: ['soc', 'roc'] },
                a4: { timepoint: ['dsch'] },
                a5: { timepoint: ['dsch'] },
                b1: { timepoint: ['soc', 'roc'] },
                b4: { timepoint: ['dsch'] },
                b5: { timepoint: ['dsch'] },
                c1: { timepoint: ['soc', 'roc'] },
                c4: { timepoint: ['dsch'] },
                c5: { timepoint: ['dsch'] },
                d1: { timepoint: ['soc', 'roc'] },
                d4: { timepoint: ['dsch'] },
                d5: { timepoint: ['dsch'] },
                z1: { timepoint: ['soc', 'roc'] },
                z4: { timepoint: ['dsch'] },
                z5: { timepoint: ['dsch'] },
            },
        },
    },
    m1870: { e1: { alias: 'm1870', timepoint: ['soc', 'roc', 'dsch'], tags: ['Q', 'PRA'] } },
    eatingSupport: { e1: { alias: 'eatingSupport', timepoint: ['soc', 'roc'] } },
    pocNutrition: { e1: { alias: 'pocNutrition', timepoint: ['soc', 'roc'] } },
};
export const SectionK = toFlatField(SectionKVersioned);
// K-K0520A1-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520A4-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520A5-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520B1-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520B4-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520B5-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520C1-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520C4-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520C5-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520D1-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520D4-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520D5-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520Z1-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520Z4-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-K0520Z5-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"0","label":"Not checked (No)"},{"value":"1","label":"Checked (Yes)"},{"value":"-","label":"Not assessed/no information"}]
// K-M1060_HEIGHT_A-format-3090 VALUES OF NUMERIC ITEMS
// Only the values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.  The submitted value must be greater than or equal to the minimum value listed in the table and less than or equal to the maximum value listed in the table, or it must match one of the remaining special values (if any) that are listed in the table.  The length of the submitted value must not exceed the allowed maximum length for the item.  Signed numbers (with a leading plus or minus sign) will not be accepted.
//  values: [{"value":"50","label":"Minimum value"},{"value":"80","label":"Maximum value"},{"value":"-","label":"Not assessed (no information)"}]
// K-M1060_HEIGHT_A-format-3100 FORMATTING OF POSITIVE INTEGER NUMERIC ITEMS
// Only positive integer values and the special values (if any) that are listed in the "Item Values" table of the Detailed Data Specifications Report will be accepted for this item.  Leading zeroes may be included or omitted from the submitted value as long as the resulting length of the string does not exceed the allowed maximum length for the item.  A decimal point and decimal values are not allowed.  A sign will not be accepted.
// The following examples are allowable if the value to be submitted is equal to [1] and the maximum length is equal to 2: [1], [01].  The following values are NOT allowed and will lead to a fatal error:  [1.], [1.0], [01.], [01.0], [1.1], [01.1], [1.01], [+1], [-2], [+1.3], [-4.5].
//  values: [{"value":"50","label":"Minimum value"},{"value":"80","label":"Maximum value"},{"value":"-","label":"Not assessed (no information)"}]
// K-M1060_WEIGHT_B-format-3090 VALUES OF NUMERIC ITEMS
// Only the values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.  The submitted value must be greater than or equal to the minimum value listed in the table and less than or equal to the maximum value listed in the table, or it must match one of the remaining special values (if any) that are listed in the table.  The length of the submitted value must not exceed the allowed maximum length for the item.  Signed numbers (with a leading plus or minus sign) will not be accepted.
//  values: [{"value":"065","label":"Minimum value"},{"value":"440","label":"Maximum value"},{"value":"-","label":"Not assessed (no information)"}]
// K-M1060_WEIGHT_B-format-3100 FORMATTING OF POSITIVE INTEGER NUMERIC ITEMS
// Only positive integer values and the special values (if any) that are listed in the "Item Values" table of the Detailed Data Specifications Report will be accepted for this item.  Leading zeroes may be included or omitted from the submitted value as long as the resulting length of the string does not exceed the allowed maximum length for the item.  A decimal point and decimal values are not allowed.  A sign will not be accepted.
// The following examples are allowable if the value to be submitted is equal to [1] and the maximum length is equal to 2: [1], [01].  The following values are NOT allowed and will lead to a fatal error:  [1.], [1.0], [01.], [01.0], [1.1], [01.1], [1.01], [+1], [-2], [+1.3], [-4.5].
//  values: [{"value":"065","label":"Minimum value"},{"value":"440","label":"Maximum value"},{"value":"-","label":"Not assessed (no information)"}]
// K-M1870_CRNT_FEEDING-format-3060 VALUES OF CODE AND CHECKLIST ITEMS
// Only the coded values listed in the "Item Values" table of the Detailed Data Specifications Report may be submitted for this item.
//  values: [{"value":"00","label":"Able to independently feed self."},{"value":"01","label":"Able to feed self independently but requires: (a) meal set-up; OR (b) intermittent assistance or supervision from another person; OR (c) a liquid, pureed or ground meat diet."},{"value":"02","label":"Unable to feed self and must be assisted or supervised throughout the meal/snack."},{"value":"03","label":"Able to take in nutrients orally and receives supplemental nutrients though a nasogastric tube or gastrostomy."},{"value":"04","label":"Unable to take in nutrients orally and is fed nutrients through a nasogastric tube or gastrostomy."},{"value":"05","label":"Unable to take in nutrients orally or by tube feeding."}]
